'use client';

import { JSX, useEffect, useState } from 'react';

import { Text } from '@videoblocks/design-system';

import { calculateAudioDuration } from '../../../../utils/calculateAudioDuration';
import CreditConfirmationModal from '../_components/CreditConfirmationModal';
import GenerateButton from '../_components/GenerateButton';
import GeneratedVoicePlayer from '../_components/GeneratedVoicePlayer';
import Modal from '../_components/Modal';
import UploadInput from '../_components/UploadInput';
import VoiceList from '../_components/VoiceList';
import { useVoiceoverContext } from '../_context/VoiceoverContext';
import { TAB_TO_EVENT_TYPE, Tabs, useVoiceoverTabContext } from '../_context/VoiceoverTabContext';

const VoiceChanger = (): JSX.Element => {
    const {
        voices,
        user,
        CreditsApi: { calculateCost },
    } = useVoiceoverContext();
    const {
        updateState,
        voiceChanger: {
            uploadedAudioUrl,
            uploadedAudioHasPlayed,
            generatedAudioUrl,
            generatedAudioHasPlayed,
            selectedVoice,
            userFile,
            aiContentId,
            lastAiContentId,
        },
    } = useVoiceoverTabContext();

    const [playingVoiceId, setPlayingVoiceId] = useState<string | null>(null);
    const [isDeleteModalOpen, setDeleteModalIsOpen] = useState(false);
    const [calculatedCost, setCalculatedCost] = useState(0);
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);

    const selectedVoiceData = voices.find((voice) => voice.voice_id === selectedVoice);

    const openDeleteModal = () => setDeleteModalIsOpen(true);
    const closeDeleteModal = () => setDeleteModalIsOpen(false);

    const handleDeleteAudio = () => {
        updateState({ uploadedAudioUrl: null, userFile: null });
        closeDeleteModal();
    };

    useEffect(() => {
        const getCalculatedCost = async () => {
            if (!userFile) return 0;
            try {
                const duration = await calculateAudioDuration(userFile);
                const cost = await calculateCost(duration);
                return Math.abs(cost);
            } catch (err) {
                console.error('calculateCost failed', err);
            }
            return 0;
        };

        void (async () => {
            const cost = await getCalculatedCost();
            setCalculatedCost(cost);
        })();
    }, [userFile, calculateCost]);

    return (
        <>
            {selectedVoiceData && (
                <CreditConfirmationModal
                    isOpen={confirmModalOpen}
                    setIsOpen={setConfirmModalOpen}
                    creditCost={calculatedCost}
                    buttonText="Generate Voice"
                    generateRequestBody={() => {
                        const formData = new FormData();
                        formData.append('file', userFile!);
                        formData.append('voiceId', selectedVoiceData.voice_id);
                        formData.append('userId', String(user?.id || ''));
                        return formData;
                    }}
                    onSuccess={() => updateState({ uploadedAudioUrl: null, userFile: null })}
                />
            )}
            <div className="text-center">
                <Text fontSize="base" fontWeight="regular" className="mb-5">
                    {uploadedAudioUrl
                        ? 'Preview your uploaded audio, then select a voice style below to transform it.'
                        : 'Upload a recording of your voice to transform it to a different voice style.'}
                </Text>
                <div className="flex flex-col items-end">
                    {uploadedAudioUrl && (
                        <GeneratedVoicePlayer
                            audioUrl={uploadedAudioUrl}
                            hasPlayed={uploadedAudioHasPlayed}
                            setHasPlayed={() => updateState({ uploadedAudioHasPlayed: true })}
                            title="Uploaded Audio"
                            onDelete={openDeleteModal}
                        />
                    )}
                    {generatedAudioUrl && (
                        <div className="w-full mb-5">
                            <GeneratedVoicePlayer
                                audioUrl={generatedAudioUrl}
                                hasPlayed={generatedAudioHasPlayed}
                                setHasPlayed={() => updateState({ generatedAudioHasPlayed: true })}
                                title="Generated Audio"
                                aiContentId={aiContentId}
                                voiceId={selectedVoice}
                                aiContentVoiceoverType={TAB_TO_EVENT_TYPE[Tabs.VoiceChanger]}
                                lastAiContentId={lastAiContentId}
                            />
                        </div>
                    )}
                    {uploadedAudioUrl && (
                        <>
                            <GenerateButton
                                className="mt-4"
                                onSubmit={() => setConfirmModalOpen(true)}
                                calculatedCost={calculatedCost}
                            >
                                Generate Voice
                            </GenerateButton>
                        </>
                    )}
                </div>

                {!uploadedAudioUrl && (
                    <UploadInput
                        handleFileUpload={(file: File) => {
                            const uploadedUrl = URL.createObjectURL(file);
                            updateState({
                                uploadedAudioUrl: uploadedUrl,
                                generatedAudioUrl: null,
                                userFile: file,
                            });
                        }}
                    />
                )}
            </div>
            <VoiceList
                selectedVoice={selectedVoice}
                setSelectedVoice={(voiceId) => updateState({ selectedVoice: voiceId })}
                playingVoiceId={playingVoiceId}
                setPlayingVoiceId={setPlayingVoiceId}
            />
            <Modal
                title="Delete audio and voiceover?"
                body={
                    <>
                        <Text fontWeight="regular">
                            Removing your uploaded audio will also delete your AI-generated voiceover.
                        </Text>
                        <Text fontWeight="regular">
                            {`If you continue, you'll need to start fresh from the beginning.`}
                        </Text>
                        <Text fontWeight="regular">{`This action is permanent and can't be undone.`}</Text>
                    </>
                }
                primaryActionText="Delete audio & voiceover"
                isOpen={isDeleteModalOpen}
                onClose={closeDeleteModal}
                onConfirm={handleDeleteAudio}
            />
        </>
    );
};

export default VoiceChanger;
